import React from 'react';
import { Card, Tag, Typography } from 'antd';
import { TriangleAlert } from 'lucide-react';
import { MAX_STRUCTURE_SCORE } from '@evaltick/shared';
import type { AssessmentResponse } from '@evaltick/shared';
import { scoreToLabel, scoreToTagColor } from '@/utils';
import styles from './AssessmentSummary.module.scss';

const { Text, Title } = Typography;

interface AssessmentSummaryProps {
  response: AssessmentResponse;
}

export const AssessmentSummary: React.FC<AssessmentSummaryProps> = ({ response }) => {
  const {
    overallScore,
    competencyScore,
    structureScore,
    reviewFlagged,
    reviewReasons,
    rationale,
  } = response;

  const overall = overallScore != null ? Math.round(overallScore) : null;
  const competencyTotal = competencyScore != null ? Math.round(competencyScore) : null;

  return (
    <div className={styles.container}>
      {reviewFlagged && (
        <Card className={styles.reviewBanner}>
          <div className={styles.reviewHeader}>
            <TriangleAlert className={styles.reviewIcon} style={{ width: '1em', height: '1em' }} />
            <Text strong>Mandatory assessor review required</Text>
          </div>
          {reviewReasons && reviewReasons.length > 0 && (
            <ul className={styles.reviewReasons}>
              {reviewReasons.map((reason) => (
                <li key={reason}><Text type="secondary">{reason}</Text></li>
              ))}
            </ul>
          )}
        </Card>
      )}

      <Card className={styles.summaryCard}>
        <div className={styles.summaryRow}>
          <div className={styles.overallScore}>
            {overall != null ? (
              <>
                <Title level={2} className={styles.scoreNumber}>{overall}</Title>
                <Text type="secondary" className={styles.scoreLabel}>/ 100</Text>
              </>
            ) : (
              <Text type="secondary">—</Text>
            )}
            <Text className={styles.scoreSublabel}>Overall Score</Text>
          </div>

          <div className={styles.divider} />

          <div className={styles.metaScores}>
            {overall != null && (
              <div className={styles.metaItem}>
                <Text type="secondary">Band</Text>
                <Tag color={scoreToTagColor(overall)} className={styles.bandTag}>
                  {scoreToLabel(overall)}
                </Tag>
              </div>
            )}
            {competencyTotal != null && (
              <div className={styles.metaItem}>
                <Text type="secondary">Competency Score</Text>
                <Text strong>{competencyTotal} / 100</Text>
              </div>
            )}
            {structureScore != null && (
              <div className={styles.metaItem}>
                <Text type="secondary">Answer Structure</Text>
                <Text strong>{structureScore} / {MAX_STRUCTURE_SCORE}</Text>
              </div>
            )}
          </div>
        </div>

        {rationale && (
          <div className={styles.rationale}>
            <Text>{rationale}</Text>
          </div>
        )}
      </Card>
    </div>
  );
};
